import type { ProviderQuote } from '../../../features/catalog/model';
import { Icon } from '../../../components/Icon';
import { PriceTag } from '../../../components/PriceTag';
import { ProviderArtwork } from '../../../components/ProviderArtwork';
import { fmtMs, pct, pctFine } from '../../../lib/format';
import { hasLatencyMetric, hasUptimeMetric } from '../ProviderRow';
import { Marker, Role, Served, type RowTone } from './RowParts';
import { m } from '../../../paraglide/messages';

/** Where the rail runs: the centre of the order marker, which sits at the start
 *  of the row's horizontal padding (px-4, sm:px-5) and is 28px wide. */
const RAIL_X = 'left-7.5 sm:left-8.5';

/** The stretch of the rail the handoff travels in this row. */
export type RowFill = 'none' | 'lower' | 'upper';

export interface DrawRowProps {
  provider: ProviderQuote;
  grid: string;
  /** 0-based position in the call order; null for a source out of rotation */
  position: number | null;
  share: number;
  /** draw the connector: the table is in call order */
  rail: boolean;
  last: boolean;
  /** false while the row waits for its turn in the reveal */
  shown: boolean;
  tone: RowTone;
  fill: RowFill;
  /** the handoff line is drawn through this row's stretch */
  fillDrawn: boolean;
  /** the handoff line is still showing (it fades rather than retracts) */
  fillLit: boolean;
}

/** One provider in call order, as the table draws it, plus the states the play moves
 *  it through: hidden before its turn, and its part in the demonstration call. */
export const DrawRow = (props: DrawRowProps) => {
  const { provider, grid, position, share, rail, last, shown, tone, fill, fillDrawn, fillLit } = props;
  const first = position === 0;
  const lit = shown && (tone === 'answered' || (first && tone !== 'failed'));
  const uptime = hasUptimeMetric(provider) ? pctFine(provider.uptimePct) : '-';
  const latency = hasLatencyMetric(provider) ? fmtMs(provider.latencyMs) : '-';
  // Only web.scrape lanes carry it: the site refusing the page, kept out of
  // uptime and so shown under it rather than folded in.
  const blocked =
    provider.blockedPct === undefined ? null : m.endpoint_provider_blocked({ value: pctFine(provider.blockedPct) });
  const mobileFacts = [
    hasUptimeMetric(provider) ? m.endpoint_provider_uptime({ value: uptime }) : null,
    blocked,
    hasLatencyMetric(provider) ? m.endpoint_row_latency({ value: latency }) : null,
    share > 0 ? m.endpoint_row_share({ value: pct(share * 100) }) : null,
  ].filter((fact) => fact !== null);
  const railSpan = `${first ? 'top-1/2' : 'top-0'} ${last ? 'bottom-1/2' : 'bottom-0'}`;
  return (
    <a
      href={`/providers/${provider.source.id}`}
      className={`${grid} group relative min-h-17 border-b border-rule px-4 py-3 transition-[background-color] duration-300 last:rounded-b-12 last:border-b-0 hover:bg-wash sm:px-5${
        position === null ? ' opacity-60' : ''
      }${lit ? ' bg-accent-soft' : ''}`}
    >
      {rail && position !== null ? (
        <span
          aria-hidden
          className={`absolute w-px origin-top bg-accent-line transition-transform duration-150 ease-linear ${RAIL_X} ${railSpan} ${
            shown ? 'scale-y-100' : 'scale-y-0'
          }`}
        />
      ) : null}
      {rail && fill !== 'none' ? (
        <span
          aria-hidden
          className={`absolute -ml-px w-0.75 origin-top rounded-full bg-accent transition-[transform,opacity] ${RAIL_X} ${
            fill === 'lower' ? 'top-1/2 bottom-0 duration-300 ease-in' : 'top-0 bottom-1/2 delay-300 duration-300 ease-out'
          } ${fillDrawn ? 'scale-y-100' : 'scale-y-0'} ${fillLit ? 'opacity-100' : 'opacity-0'}`}
        />
      ) : null}
      <span
        className={`relative z-10 grid place-items-center transition-opacity duration-300 ${shown ? 'opacity-100' : 'opacity-0'}`}
      >
        <Marker position={position} tone={tone} />
      </span>
      <span
        className={`[grid-column:2/-1] grid grid-cols-subgrid items-center transition-[opacity,translate] duration-300 ease-out ${
          shown ? 'translate-y-0 opacity-100' : '-translate-y-1.5 opacity-0'
        }`}
      >
        <span className="grid place-items-center">
          <ProviderArtwork source={provider.source} size={first ? 40 : 32} />
        </span>
        <span className="flex min-w-0 flex-col gap-1">
          <span
            className={`truncate group-hover:text-accent ${first ? 'text-15 font-semibold text-text' : 'text-14 font-medium text-text-2'}`}
          >
            {provider.source.name}
          </span>
          <Role position={position} tone={tone} />
          {mobileFacts.length > 0 ? (
            <span className="mono flex flex-wrap items-center gap-x-3 gap-y-1 text-10 text-text-3 lg:hidden">
              {mobileFacts.map((fact) => (
                <span key={fact}>{fact}</span>
              ))}
            </span>
          ) : null}
        </span>
        <span className="hidden lg:block">
          <Served provider={provider} share={share} first={first} />
        </span>
        <span className="mono hidden text-right text-11 text-text-3 lg:block">
          {uptime}
          {blocked === null ? null : <span className="block text-10">{blocked}</span>}
        </span>
        <span className="mono hidden text-right text-11 text-text-3 lg:block">{latency}</span>
        <span className="flex justify-end">
          <PriceTag price={provider.price} size={first ? 'md' : 'sm'} explain flatNote="flat" />
        </span>
        <span className="hidden size-10 place-items-center text-text-3 group-hover:text-accent sm:grid">
          <Icon name="chevron-right" size={16} />
        </span>
      </span>
    </a>
  );
};
